import { chatKindOf } from '@bt-common/schemas/wa/chat-kind';
import RowStack from "@/common/components/common/row-stack/RowStack";
import { useAppSelector } from "@/common/reducers/hooks/store.hook";
import { makeSelectDisplayNamesByMessageId, selectMessageById } from "./redux/TeamInboxSelectors";
import InlineMetaDisplayWrapper from "@/extension/context/content/components/add-scheduled-message-button/InlineContextMessageContainer";
import TeamInboxMessage from "./team-inbox-message";
import MessageHoverActions from "./message-hover-actions";
import { getMessageText } from "./team-inbox-helpers";
import SourceBadge from "./source-badge";
import ContactAvatar from "@/extension/context/content/components/bulk-dashboard/contact-table/contact-avatar";
import React, { useMemo, useState } from "react";
import { useTranslation } from 'react-i18next';
import { Box } from "@mui/material";

type Props = {
    itemId: string;
    /** First bubble of a same-sender cluster (tail + colored name + avatar). */
    isFirstOfCluster?: boolean;
    /** Last bubble of a same-sender cluster. */
    isLastOfCluster?: boolean;
}

const AVATAR_SIZE = 28;
const AVATAR_GAP = 6;

const WaMessagesDisplayRow = React.memo(({ itemId, isFirstOfCluster = true, isLastOfCluster = true }: Props) => {
    const { t } = useTranslation();
    const message = useAppSelector(state => selectMessageById(state, itemId));
    const waSessionPhoneNumber = useAppSelector(state => state.TeamInboxReducer?.waSessionPhoneNumber ?? null);
    const [hovered, setHovered] = useState(false);
    // Cursor position of a right-click on the bubble — opens the context menu
    // (rendered inside MessageHoverActions) anchored at the cursor, WA-style.
    const [contextMenuPosition, setContextMenuPosition] = useState<{ top: number; left: number } | null>(null);

    // Memoize the display names selector
    const selectDisplayNames = useMemo(
        () => makeSelectDisplayNamesByMessageId(itemId),
        [itemId]
    );
    const displayNames = useAppSelector(selectDisplayNames);

    if (!message) {
        return <div style={{ height: '1px' }} />;
    }

    // Deleted messages render a WA-style placeholder bubble instead of
    // collapsing (better parity than the old 1px hide).
    const isDeleted = !!message.deleted;
    const isSystem = message.type === "gp2";
    // Debug-only badges (same flag as the message-key display in the bubble).
    const showDebugBadges = typeof localStorage !== 'undefined' && localStorage.getItem('bt-debug') === 'true';
    const fromMe = !!message.fromMe;
    const isGroupChat = chatKindOf(message.chatId) === 'group';
    // Group avatar: shown for received group messages, anchored at the FIRST
    // bubble of the sender's cluster (WA aligns it to the top of that bubble).
    const showAvatarColumn = isGroupChat && !fromMe && !isSystem;
    const authorId: string | undefined = message.author?._serialized ?? message.author ?? undefined;

    let justifyContent: "flex-start" | "flex-end" | "center" = "flex-start";
    if (isSystem) {
        justifyContent = "center";
    } else if (fromMe) {
        justifyContent = "flex-end";
    }

    // Cluster spacing: tight (2px) within a same-sender run, roomy (10px) between
    // different senders / date groups.
    const marginTop = isFirstOfCluster ? '10px' : '2px';

    return (
        <RowStack
            // Hover on the WHOLE row keeps the action bar alive — the bar floats
            // 6px away from the bubble, so bubble-only hover would drop `hovered`
            // (and hide the bar) while the pointer crosses the gap toward it.
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            sx={{
                display: 'flex',
                justifyContent: justifyContent,
                alignItems: 'flex-start',
                margin: `${marginTop} 5% 0 5%`,
                hyphens: 'none',
            }}>

            {/* 'userMessage' */}
            {!isSystem && (
                <Box sx={{
                    display: 'flex',
                    flexDirection: fromMe ? 'row-reverse' : 'row',
                    alignItems: 'flex-start',
                    gap: `${AVATAR_GAP}px`,
                    // WhatsApp caps the bubble ~65% of the chat width (avatar column
                    // sits inside this cap in group chats).
                    maxWidth: '65%',
                }}>
                    {/* Group avatar column (or reserved spacer so bubbles align) */}
                    {showAvatarColumn && (
                        <Box sx={{ width: AVATAR_SIZE, flexShrink: 0, alignSelf: 'flex-start' }}>
                            {isFirstOfCluster && authorId && (
                                <ContactAvatar contactId={authorId} size={AVATAR_SIZE} textSize={12} />
                            )}
                        </Box>
                    )}

                    <Box
                        sx={{ position: 'relative', maxWidth: '100%' }}
                        onContextMenu={isDeleted ? undefined : (e) => {
                            e.preventDefault();
                            setContextMenuPosition({ top: e.clientY, left: e.clientX });
                        }}
                    >
                        {isDeleted ? (
                            /* WA-style deleted placeholder bubble */
                            <Box sx={{
                                bgcolor: fromMe ? 'var(--whatsapp-bubble-sent)' : 'var(--whatsapp-bubble-received)',
                                borderRadius: '7.5px',
                                padding: '6px 9px 8px 9px',
                                boxShadow: '0 1px 0.5px rgba(0,0,0,0.13)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '5px',
                                fontStyle: 'italic',
                                fontSize: '14.2px',
                                lineHeight: '19px',
                                color: 'var(--wa-meta-text, #667781)',
                            }}>
                                <span style={{ fontStyle: 'normal', fontSize: '13px' }}>🚫</span>
                                <span>{t('teamInbox.messageDeleted', 'This message was deleted')}</span>
                            </Box>
                        ) : (
                            <TeamInboxMessage message={message} isFirstOfCluster={isFirstOfCluster} />
                        )}

                        {/* Tier badge (WA / VAULT / DB) — bt-debug only. Every
                            message gets one: an absent `source` means the row came
                            straight from the live WhatsApp store, so it defaults to
                            'whatsapp' (WA); vault + DB-backup rows carry their own
                            tag. Lets you watch the WhatsApp→vault→DB fallback as you
                            scroll up. */}
                        {!isDeleted && showDebugBadges && (
                            <Box sx={{
                                position: 'absolute',
                                top: '-8px',
                                right: fromMe ? 'auto' : '-8px',
                                left: fromMe ? '-8px' : 'auto'
                            }}>
                                <SourceBadge source={message.source || 'whatsapp'} size="small" />
                            </Box>
                        )}

                        {/* Hover action bar (React / Forward / Note / Task / Reminder / More)
                            + the shared context menu (chevron or right-click) */}
                        {!isDeleted && (
                            <MessageHoverActions
                                message={message}
                                fromMe={fromMe}
                                hovered={hovered}
                                contextMenuPosition={contextMenuPosition}
                                onContextMenuClose={() => setContextMenuPosition(null)}
                            />
                        )}
                    </Box>
                </Box>
            )}

            {/* 'systemMessage' */}
            {isSystem && (
                <InlineMetaDisplayWrapper>
                    {getMessageText(message, displayNames, waSessionPhoneNumber ?? '')}
                    {showDebugBadges && <SourceBadge source={message.source || 'whatsapp'} size="small" />}
                </InlineMetaDisplayWrapper>
            )}
        </RowStack>
    )
});

export default WaMessagesDisplayRow;
